import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { InvoiceListResponse } from './contractsMirror.js';
import type { InvoicesSearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE invoices endpoint (the strangler-fig +
 * coarse-endpoint proof, CARVE-07). One query key per `{show_archived}` — toggling
 * archived triggers exactly one refetch of `GET /api/v1/invoices?show_archived=…`.
 * The SERVER computes the enriched rows + the Outstanding/Overdue rollups in a
 * single GROUP BY (21-02); the SPA only renders the returned `InvoiceListResponse`
 * (no client re-aggregation, no new N+1 — threat T-21-SEC-11 / CARVE-07).
 *
 * The request goes through `apiFetch` (the only fetch path — `credentials:'include'`
 * + `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is
 * derived server-side from the auth context; the SPA NEVER sends a tenant id param
 * (threat T-21-SEC-11).
 */

/** Build the stable query key for a filter (one cache entry per archived state). */
export function invoicesQueryKey(search: InvoicesSearch): readonly unknown[] {
  return ['invoices', search.show_archived ?? false] as const;
}

/** Build the coarse-endpoint request path with the active filter (no tenant param). */
export function invoicesRequestPath(search: InvoicesSearch): string {
  return search.show_archived ? '/api/v1/invoices?show_archived=1' : '/api/v1/invoices';
}

export function useInvoicesQuery(search: InvoicesSearch) {
  return useQuery<InvoiceListResponse>({
    queryKey: invoicesQueryKey(search),
    queryFn: () => apiFetchJson<InvoiceListResponse>(invoicesRequestPath(search)),
  });
}
